import { Box, Typography } from '@mui/material';
import { Fragment, JSX, ReactNode } from 'react';
import {
    cardActionsStyles,
    cardFieldLabelStyles,
    cardFieldValueStyles,
    cardFieldsStyles,
    cardHeaderStyles,
    cardStyles,
    cardTitleStyles,
} from './recordCard.styles';

export interface IRecordCardField {
    /** Already translated by the caller -- the column heading the field came from. */
    label: string;
    value: ReactNode;
}

interface IRecordCardProps {
    title: ReactNode;
    /** Sits with the title rather than among the fields: a status qualifies the name. */
    titleAdornment?: ReactNode;
    fields: IRecordCardField[];
    actions?: ReactNode;
    /** Still listed, no longer live. See `cardStyles`. */
    isMuted?: boolean;
}

/**
 * One row of a table, drawn as a card.
 *
 * Below `md` a table stops being readable: the widest of ours needs 762px of columns, so on
 * a phone the last column -- which is where the button that opens the record lives -- sits
 * a screen and a half past the right edge, reachable only by somebody who guesses it is
 * there. A card puts the same facts in a column and the action under them.
 *
 * Shared rather than written six times so the six lists cannot drift into showing the same
 * kind of record in six different shapes -- the same reason `PatientTherapists` and
 * `MembershipStatusChip` are shared. It takes already-translated labels: the callers hand
 * over the very `ResourcesIds` keys their table headings use, so the two views of one list
 * always name a field the same way.
 */
export const RecordCard = ({
    title,
    titleAdornment,
    fields,
    actions,
    isMuted = false,
}: IRecordCardProps): JSX.Element => (
    <Box sx={cardStyles(isMuted)}>
        <Box sx={cardHeaderStyles}>
            <Typography variant="subtitle1" sx={cardTitleStyles}>
                {title}
            </Typography>
            {titleAdornment}
        </Box>

        <Box sx={cardFieldsStyles}>
            {fields.map(field => (
                // A fragment rather than a wrapper: the label and its value have to be two
                // direct children of the grid, so that every value on the card lines up on
                // one column however long the labels are. Anything boxing them together
                // would make the pair a single cell and take that alignment away.
                <Fragment key={field.label}>
                    <Typography component="span" sx={cardFieldLabelStyles}>
                        {field.label}
                    </Typography>
                    {/* A div rather than a span: a value may be an interactive control -- the
                        therapists table's editable share is one -- and MUI renders those as
                        divs, which are not legal inside phrasing content. Still one direct
                        child of the grid, so the alignment above is untouched. */}
                    <Box sx={cardFieldValueStyles}>
                        {field.value}
                    </Box>
                </Fragment>
            ))}
        </Box>

        {actions && <Box sx={cardActionsStyles}>{actions}</Box>}
    </Box>
);
